<template>
  <div class="time-dilation-pro">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$t = t₀ / √(1 - 2GM/rc²)$</div>
    </div>

    <div class="main-layout vertical">
      <!-- 仿真演示区 -->
      <div class="simulation-stage dark-stage">
        <div class="hud-panel">
          <div class="hud-item cyan">
            <label>远端观测频率 (f₀)</label>
            <span class="val">{{ baseFreq.toFixed(2) }} <small>Hz</small></span>
          </div>
          <div class="hud-item pink">
            <label>近场实测频率 (f)</label>
            <span class="val">{{ (baseFreq / timeFactor).toFixed(2) }} <small>Hz</small></span>
          </div>
        </div>

        <svg width="100%" height="240" viewBox="0 0 400 240" class="warp-field">
          <!-- 时空背景网格 -->
          <g v-for="i in 8" :key="'w'+i">
            <circle cx="200" cy="120" :r="25 + i*25" fill="none" 
                    :stroke="gridColor" :stroke-width="0.5" 
                    :opacity="1 - i*0.1" />
          </g>

          <!-- 中心大质量天体 (黑洞/中性星) -->
          <g transform="translate(200, 120)">
            <circle r="25" fill="#000" stroke="#3b82f6" stroke-width="1.5" />
            <circle r="40" fill="url(#event-horizon)" opacity="0.6" />
            <text y="55" text-anchor="middle" fill="#94a3b8" font-size="10" font-weight="700">强引力源</text>
          </g>

          <!-- 远端参考时钟 -->
          <g transform="translate(60, 60)">
            <rect x="-35" y="-35" width="70" height="70" rx="10" fill="rgba(15,23,42,0.8)" stroke="#334155" />
            <circle r="25" fill="none" stroke="#64748b" stroke-width="1.5" />
            <line :x2="22 * Math.cos(time)" :y2="22 * Math.sin(time)" stroke="#22d3ee" stroke-width="2.5" stroke-linecap="round" />
            <text y="48" text-anchor="middle" fill="#94a3b8" font-size="10" font-weight="700">远端：正常</text>
          </g>

          <!-- 近场受激时钟 -->
          <g :transform="`translate(${200 + 40 + mass}, 120)`">
            <rect x="-35" y="-35" width="70" height="70" rx="10" fill="rgba(15,23,42,0.8)" stroke="#f43f5e" stroke-width="1.5" />
            <circle r="25" fill="none" stroke="#f43f5e" stroke-width="1.5" opacity="0.3" />
            <line :x2="22 * Math.cos(dilatedTime)" :y2="22 * Math.sin(dilatedTime)" stroke="#f43f5e" stroke-width="2.5" stroke-linecap="round" />
            <text y="48" text-anchor="middle" fill="#f43f5e" font-size="10" font-weight="700">近场：变慢</text>
          </g>

          <defs>
            <radialGradient id="event-horizon">
              <stop offset="0%" stop-color="#3b82f6" stop-opacity="0.8" />
              <stop offset="100%" stop-color="#3b82f6" stop-opacity="0" />
            </radialGradient>
          </defs>
        </svg>
      </div>

      <!-- 控制面板 -->
      <div class="compact-controls">
        <div class="control-card-grid">
          <div class="control-item">
            <div class="label-row">
              <span class="label">引力强度 (M)</span>
              <span class="val">{{ mass }} UNITS</span>
            </div>
            <el-slider v-model="mass" :min="10" :max="100" size="small"></el-slider>
          </div>
          <div class="control-item">
            <div class="label-row">
              <span class="label">时间流加速</span>
              <span class="val">{{ playbackSpeed.toFixed(1) }}x</span>
            </div>
            <el-slider v-model="playbackSpeed" :min="1" :max="5" :step="0.5" size="small"></el-slider>
          </div>
        </div>

        <div class="discovery-card-compact" v-latex>
          <i class="el-icon-info"></i>
          <p><strong>物理规律：</strong>引力不仅弯曲空间，也<strong>弯曲时间</strong>。在强引力场（如黑洞附近）中，时空被极度压缩，导致时钟的滴答声在外界看来变得缓慢。这一效应并非错觉，而是物理实在，GPS 卫星必须经过引力时间修正才能精确导航。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'GravitationalTimeDilationViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      mass: 40,
      playbackSpeed: 1,
      time: 0,
      baseFreq: 1.0,
      running: true
    }
  },
  computed: {
    timeFactor() {
      // Proxy formula for time dilation
      return 1 + (this.mass * 0.02);
    },
    dilatedTime() {
      return this.time / this.timeFactor;
    },
    gridColor() {
       return this.mass > 70 ? '#3b82f6' : '#1e293b';
    }
  },
  methods: {
    update() {
      if (!this.running) return;
      this.time += 0.05 * this.playbackSpeed;
      this.frameId = requestAnimationFrame(this.update);
    }
  },
  mounted() { this.update(); },
  beforeDestroy() {
    this.running = false;
    cancelAnimationFrame(this.frameId);
  }
}
</script>

<style scoped lang="scss">
.time-dilation-pro { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }

.viz-header-compact {
  display: flex; justify-content: space-between; align-items: center; padding: 0 4px;
  .brief { font-size: 13.5px; color: #1e293b; font-weight: 500; }
  .formula-chip { background: #eff6ff; color: #1d4ed8; border: 1px solid #bfdbfe; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; }
}

.simulation-stage {
  flex: 1; min-height: 240px; background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden;
  .hud-panel {
    position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; z-index: 10;
    .hud-item {
      background: rgba(15, 23, 42, 0.8); border-radius: 8px; border: 1px solid #1e293b; padding: 4px 12px;
      label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; }
      .val { font-size: 14px; color: #f1f5f9; font-weight: 800; font-family: monospace; }
      &.cyan { border-left: 3px solid #06b6d4; }
      &.pink { border-left: 3px solid #f43f5e; }
    }
  }
}

.compact-controls { display: flex; flex-direction: column; gap: 8px; }
.control-card-grid {
  display: grid; grid-template-columns: 1fr 1fr; gap: 12px; background: #ffffff; padding: 10px 14px; border-radius: 12px; border: 1px solid #e2e8f0;
  .control-item {
    .label-row { display: flex; justify-content: space-between; .label { font-size: 12px; color: #64748b; } .val { font-size: 12px; color: #2563eb; font-weight: 700; } }
  }
}

.discovery-card-compact {
  background: #f8fafc; padding: 10px 14px; border-radius: 10px; border: 1px solid #e2e8f0; display: flex; align-items: center; gap: 10px;
  i { color: #2563eb; font-size: 1.1rem; }
  p { flex: 1; margin: 0; font-size: 12px; color: #475569; line-height: 1.5; strong { color: #1e293b; } }
}
</style>
